<template>
  <section-component
    v-if="hasData"
    :title="title"
    :link="link"
    :total="total"
    :isTitleClickable="false"
    class="related-section"
  >
    <div class="related-section__category-cards category-cards">
      <div v-for="card in cards" :key="card.title" class="category-cards__item">
        <product-item :item="card" @click="$emit('itemClicked')" />
      </div>
    </div>
  </section-component>
</template>
<script>
import arraySort from 'array-sort'

import ProductItem from '@/components/common/ProductItem.vue'

import SectionComponent from '@/components/home/ui/SectionComponent.vue'

import { useSearch } from '@/composables/useSearch'
import { useSortingMethods } from '@/composables/useSortingMethods'
import { useTransformEntries } from '@/composables/useTransformEntries'

export default {
  name: 'FeaturedResources',
  components: {
    ProductItem,
    SectionComponent,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['itemClicked'],
  setup() {
    const { withSortByFeaturedAndDate } = useSortingMethods()
    const { getFacetValueFullName, fetchResources } = useSearch()
    const { transformEntries } = useTransformEntries()

    return {
      withSortByFeaturedAndDate,
      getFacetValueFullName,
      transformEntries,
      fetchResources,
    }
  },
  data() {
    return {
      title: '',
      cards: [],
      link: {},
      total: 0,
      defaultResourcesFilters: [
        'contentType:resource',
        'NOT resourceGroup:"Press Releases"',
        'NOT resourceGroup:"Events"',
        'NOT resourceGroup:"Ready-to-ship Inventory"',
        'NOT resourceGroup:"FAQ"',
      ],
      maxCards: 6,
    }
  },
  computed: {
    hasData() {
      return !!this.cards.length
    },
  },
  created() {
    this.initData()
  },
  methods: {
    async initData() {
      const pageTitle = this.fields.pageNameFilter ? this.fields.pageNameFilter.trim() : ''
      let resources = []
      let total = 0
      let pageFacet = ''
      if (pageTitle) {
        const { hits, nbHits, pageFacetString } = await this.getResourcesByPageTitle(pageTitle)
        resources = hits.slice(0, this.maxCards)
        total = nbHits
        pageFacet = pageFacetString
      } else {
        resources = this.fields.items
      }

      const sortedFeatured = arraySort(resources, this.withSortByFeaturedAndDate(true))
      const sortedResources = this.transformEntries(sortedFeatured)

      const deepestPageCategory = pageTitle.split(' > ').pop()
      this.title = this.fields.sectionTitle || `Featured Resources on ${deepestPageCategory}`
      this.cards = sortedResources
      this.link = pageTitle
        ? this.fields.link?.fields || {
            title: 'View All',
            link:
              this.$contentful.service.getEducationalContentPage().link +
              `?facets=(${encodeURIComponent(pageFacet)})`,
            buttonSize: 'Small',
          }
        : this.fields.link?.fields || null
      this.total = total
    },
    async getResourcesByPageTitle(pageTitle) {
      const { facets: allResourcesFacets } = await this.fetchResources({
        facets: ['*'],
        filters: this.defaultResourcesFilters.join(' AND '),
        hitsPerPage: 1,
      })
      const getPageFacet = (resFacets) => {
        const pageTitleAsFacet = pageTitle
        const value = Object.keys(resFacets)
          .map((facetName) =>
            Object.keys(resFacets[facetName]).filter((el) => el === pageTitleAsFacet),
          )
          .filter((el) => el.length)
          .flat(1)[0]
        const facet = Object.keys(resFacets).filter((facet) => resFacets[facet][value])[0]
        return this.getFacetValueFullName(facet, value)
      }
      const pageFacet = getPageFacet(allResourcesFacets)
      const { hits, nbHits } = await this.fetchResources({
        filters: [...this.defaultResourcesFilters, pageFacet].join(' AND '),
        hitsPerPage: this.maxCards,
      })
      return {
        hits,
        nbHits,
        pageFacetString: pageFacet,
      }
    },
  },
}
</script>
